import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import { router } from '@/router'
import { setupDirectives } from '@/directives'
import apiClient from '@/services/api'
import { useUserStore } from '@/stores/user'
import { useThemeStore } from '@/stores/theme'
import { useEcho } from '@/composables/useEcho'
import { initI18n } from '@/composables/useI18n'
import { installErrorReporting } from '@/utils/errorReporter'

import './styles/tailwind.css'

// Provide global apiClient on window for any legacy or dynamic callers
if (typeof window !== 'undefined') {
  ;(window as any).axios = apiClient
  ;(window as any).apiClient = apiClient
}

const app = createApp(App)
installErrorReporting(app, 'storefront')
const pinia = createPinia()

app.use(pinia)
app.use(router)
setupDirectives(app)

// Initialize user session & live theme tokens
const userStore = useUserStore()
userStore.fetchUser().catch(() => {})

const themeStore = useThemeStore()
themeStore.initTheme().catch(() => {})

// Connect to the real-time WebSocket server (public, unauthenticated feed —
// bid counts and prices update live for every visitor, logged in or not)
useEcho().initialize()

// After a deploy the previous build's chunks are gone, so a tab opened before
// it fails to load the next lazy page. Reload once to pick up the new build;
// the timestamp stops a reload loop if the chunk is missing for another reason.
function reloadForNewBuild(target?: string) {
  try {
    const last = Number(sessionStorage.getItem('itorgo_chunk_reload') || 0)
    if (Date.now() - last < 10_000) return false
    sessionStorage.setItem('itorgo_chunk_reload', String(Date.now()))
  } catch { /* storage blocked: still reload once */ }
  if (target) window.location.assign(target)
  else window.location.reload()
  return true
}
window.addEventListener('vite:preloadError', event => {
  if (reloadForNewBuild()) event.preventDefault()
})
router.onError((error, to) => {
  if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(String(error?.message || error))) {
    reloadForNewBuild(to?.fullPath)
  }
})

// Mount once the on-screen language is loaded (its own chunk).
initI18n().finally(() => app.mount('#app'))